import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import "@tiptap/pm/state"; // This is an implicit dependency of @tiptap/react and needs to be explicitly imported here
import classNames from "classnames";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import { ALL_FORMAT_OPTIONS, EditorToolbar, } from "./EditorToolbar";
import { useRef } from "react";
import { useAddLinkPopover } from "./useAddLinkPopover";
import { EditLinkPopover, useEditLinkPopover } from "./useEditLinkPopover";
import { debounce } from "lodash";
import { InputWrapper } from "../inputs/InputWrapper";
import { Highlight } from "./Highlight";
import { SizedParagraph } from "./SizedParagraph";
import { SizedSpan } from "./SizedSpan";
import sanitizeHtmlDescription from "./sanitizeHtmlDescription";
import { textAlignSupportedTypes } from "./TextAlignDropdown";
export const RichTextEditor = (internalProps) => {
    var _a, _b, _c, _d;
    const props = Object.assign(Object.assign({}, internalProps), { isDisabled: (_a = internalProps.isDisabled) !== null && _a !== void 0 ? _a : false, testId: (_b = internalProps.testId) !== null && _b !== void 0 ? _b : "", excludeMenuItems: (_c = internalProps.excludeMenuItems) !== null && _c !== void 0 ? _c : [], defaultTextSize: (_d = internalProps.defaultTextSize) !== null && _d !== void 0 ? _d : "body14" });
    const editorRef = useRef(null);
    const editorId = props.id || "rich-text-editor";
    const textSizeExtentions = !props.excludeMenuItems.includes("textSize") ||
        internalProps.defaultTextSize
        ? [
            SizedParagraph.configure({
                defaultTextSize: props.defaultTextSize,
            }),
            SizedSpan.configure({
                defaultTextSize: props.defaultTextSize,
            }),
        ]
        : [];
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                bulletList: {
                    HTMLAttributes: {
                        class: "!list-disc list-inside [&>li>p]:inline [&>li]:ml-2 [&>li::marker]:mr-0",
                        keepAttributes: true,
                        keepMarks: true,
                    },
                },
            }),
            Link.configure({
                linkOnPaste: false,
                openOnClick: false,
                HTMLAttributes: {
                    class: "[&>a]:text-text-link underline",
                },
            }),
            Highlight,
            TextAlign.configure({
                types: textAlignSupportedTypes,
                alignments: ["left", "center", "right"],
            }),
            ...textSizeExtentions,
        ],
        onUpdate: () => {
            var _a;
            const html = editor === null || editor === void 0 ? void 0 : editor.getHTML();
            if (html) {
                const sanitizedHtml = sanitizeHtmlDescription(html);
                (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, sanitizedHtml);
            }
        },
        editorProps: {
            attributes: {
                class: classNames("p-1 rounded focus:outline-border-brand focus-visible:outline-border-brand w-full h-full max-h-[500px] overflow-auto cursor-text", props.isDisabled && "pointer-events-none", props.editorClassName, "[&_.text-size-footer-12]:text-[12px] [&_.text-size-footer-12]:leading-[16px] [&_.text-size-footer-12]:font-normal", "[&_.text-size-body-14]:text-[14px] [&_.text-size-body-14]:leading-[20px] [&_.text-size-body-14]:font-normal", "[&_.text-size-body-16]:text-[16px] [&_.text-size-body-16]:leading-[24px] [&_.text-size-body-16]:font-normal", "[&_.text-size-body-18]:text-[18px] [&_.text-size-body-18]:leading-[28px] [&_.text-size-body-18]:font-normal", "[&_.text-size-subheader-24]:text-[24px] [&_.text-size-subheader-24]:leading-[32px] [&_.text-size-subheader-24]:font-normal", "[&_.text-size-header-32]:text-[32px] [&_.text-size-header-32]:leading-[40px] [&_.text-size-header-32]:font-normal", "[&_.text-size-header-40]:text-[40px] [&_.text-size-header-40]:leading-[56px] [&_.text-size-header-40]:font-normal", "[&_.text-size-header-48]:text-[48px] [&_.text-size-header-48]:leading-[56px] [&_.text-size-header-48]:font-normal", "[&_.text-size-header-56]:text-[56px] [&_.text-size-header-56]:leading-[64px] [&_.text-size-header-56]:font-normal", "[&_.text-size-title-64]:text-[64px] [&_.text-size-title-64]:leading-[72px] [&_.text-size-title-64]:font-normal", "[&_.text-size-title-72]:text-[72px] [&_.text-size-title-72]:leading-[80px] [&_.text-size-title-72]:font-normal", "[&_.text-size-title-80]:text-[80px] [&_.text-size-title-80]:leading-[88px] [&_.text-size-title-80]:font-normal"),
                id: editorId,
                "data-test": props.testId,
            },
        },
        content: props.initialContent,
        editable: !props.isDisabled,
    }, [props.isDisabled]);
    const { AddLinkPopover, addLinkPopoverProps, showLinkPopover, onClickEditLink, editorToolbarProps, resetAddLinkPopover, } = useAddLinkPopover({
        editor,
        editorRef,
    });
    const { showEditLinkPopover, editLinkPopoverProps, hideEditLinkPopover, onEditorClickCallback, } = useEditLinkPopover({
        editor,
        onClickEditLink,
    });
    const debouncedClick = debounce((e) => {
        onEditorClickCallback(e);
    }, 150);
    const formatOptions = ALL_FORMAT_OPTIONS.filter((formatOption) => !props.excludeMenuItems.includes(formatOption));
    return (_jsx(InputWrapper, Object.assign({}, internalProps, { labelHtmlFor: editorId, render: ({ inputClasses }) => (_jsxs("div", { ref: editorRef, className: classNames(inputClasses, !props.errorText && "border-border-regular", "border rounded max-w-[1200px] min-w-[288px] !p-0"), children: [_jsx(EditorToolbar, Object.assign({ editor: editor }, editorToolbarProps, { hideEditLinkPopover: hideEditLinkPopover, isEditorDisabled: props.isDisabled, formatOptions: formatOptions, defaultTextSize: props.defaultTextSize })), _jsx(EditorContent, { editor: editor, onClick: (e) => {
                        resetAddLinkPopover();
                        debouncedClick(e);
                    } }), showLinkPopover ? _jsx(AddLinkPopover, Object.assign({}, addLinkPopoverProps)) : null, showEditLinkPopover ? (_jsx(EditLinkPopover, Object.assign({}, editLinkPopoverProps))) : null] })) })));
};
//# sourceMappingURL=RichTextEditor.js.map